import {
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useLayoutEffect,
  useRef,
} from 'react';
import { SendHorizontal, ShieldCheck } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectDriverChat,
  setDriverChatDraft,
} from '@store/slices/driverChat/driverChatSlice';
import { useGetDriverChatQuickRepliesQuery } from '@store/rtk/driverChat';
import {
  DRIVER_CHAT_COUNTER_THRESHOLD,
  DRIVER_CHAT_MESSAGE_LIMIT,
  DRIVER_CHAT_QUICK_REPLIES,
} from '@component/DriverChat/constants';

type ChatComposerProps = {
  conversationId: number;
  driverFirstName: string;
  disabled: boolean;
  disabledPlaceholder?: string;
  onSend: (text: string) => void;
  dataTestId: string;
};

const MAX_TEXTAREA_HEIGHT = 120;

export const ChatComposer = forwardRef<
  HTMLTextAreaElement,
  ChatComposerProps
>(
  (
    {
      conversationId,
      driverFirstName,
      disabled,
      disabledPlaceholder,
      onSend,
      dataTestId,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { data: quickReplies } = useGetDriverChatQuickRepliesQuery();
    const replies = quickReplies?.length ? quickReplies : DRIVER_CHAT_QUICK_REPLIES;
    const draft = useAppSelector(selectDriverChat).drafts[conversationId] ?? '';
    const localRef = useRef<HTMLTextAreaElement | null>(null);
    const isOverLimit = draft.length > DRIVER_CHAT_MESSAGE_LIMIT;
    const canSend = !disabled && !!draft.trim() && !isOverLimit;
    const textareaId = `dc-composer-${conversationId}`;

    useLayoutEffect(() => {
      const textarea = localRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
    }, [draft]);

    const setDraft = (text: string) =>
      dispatch(setDriverChatDraft({ conversationId, text }));

    const submit = (text: string) => {
      const trimmed = text.trim();
      if (disabled || !trimmed || trimmed.length > DRIVER_CHAT_MESSAGE_LIMIT) {
        return;
      }
      onSend(trimmed);
      setDraft('');
    };

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        submit(draft);
      }
    };

    return (
      <div className="dc-composer" data-test-id={`${dataTestId}-composer`}>
        <div className="dc-composer__quick" role="group" aria-label="Quick replies">
          {replies.map((reply) => (
            <button
              key={reply}
              type="button"
              disabled={disabled}
              onClick={() => onSend(reply)}
            >
              {reply}
            </button>
          ))}
        </div>

        <div className={`dc-composer__box ${disabled ? 'is-disabled' : ''}`}>
          <label className="dc-sr-only" htmlFor={textareaId}>
            Message {driverFirstName}
          </label>
          <textarea
            id={textareaId}
            ref={(node) => {
              localRef.current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            rows={1}
            value={draft}
            disabled={disabled}
            placeholder={
              disabled && disabledPlaceholder
                ? disabledPlaceholder
                : `Message ${driverFirstName}…`
            }
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setDraft(event.target.value)
            }
            onKeyDown={onKeyDown}
            data-test-id={`${dataTestId}-composer-input`}
          />
          <button
            type="button"
            className="dc-composer__send"
            aria-label="Send message"
            disabled={!canSend}
            onClick={() => submit(draft)}
            data-test-id={`${dataTestId}-composer-send`}
          >
            <SendHorizontal size={17} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <div className="dc-composer__foot">
          <span>
            <ShieldCheck size={13} strokeWidth={2} aria-hidden="true" />
            Logged for audit · share only the minimum PHI needed
          </span>
          {draft.length >= DRIVER_CHAT_COUNTER_THRESHOLD && (
            <span
              className={`dc-composer__count ${isOverLimit ? 'is-over' : ''}`}
              aria-live="polite"
            >
              {draft.length}/{DRIVER_CHAT_MESSAGE_LIMIT}
            </span>
          )}
        </div>
      </div>
    );
  }
);

ChatComposer.displayName = 'ChatComposer';
